/**
 * Casual Competitors - 头部厂商下载配置
 * @description 构建头部厂商模块的 SSE 下载参数
 * @note 每个厂商卡片独立下载，只下载该厂商的数据
 * @note Companies 标签页和 Games 标签页分别调用不同的接口
 */
import { omit } from 'lodash-es';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { shouldUseSSE } from '@intelligence/components/DownloadCommon/DownloadPage/utils/sse';

const groupByGenerators = [
  {
    formModelKey: DOWNLOAD_CFG_KEY.DATE_METHOD,
    groupByKey: 'date',
    condition: (payload: string) => payload !== 'aggregated',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'region',
    condition: (payload: string) => payload === 'region',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'market',
    condition: (payload: string) => payload === 'market',
  },
];

const groupByFormModelKeys = groupByGenerators.map((generator) => generator?.formModelKey);

const getFileName = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const fileName = 'Competitors_Publishers';
  if (!!startDate && !!endDate) {
    return `${fileName}_${startDate}_${endDate}`;
  }
  return `${fileName}`;
};

const getSelectedCompanyIds = (params: any): string[] | undefined => {
  const pageCompanyIds = Array.isArray(params?.page_company_ids) ? params?.page_company_ids : [];
  const fallbackCompanyIds = Array.isArray(params?.filter?.company_ids) ? params?.filter?.company_ids : [];
  const sourceCompanyIds = pageCompanyIds.length > 0 ? pageCompanyIds : fallbackCompanyIds;
  const isSelectTopDownload = params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] === 'selectTop';
  const selectTop = Number(params?.[DOWNLOAD_CFG_KEY.SELECT_TOP]);
  const hasValidSelectTop = Number.isFinite(selectTop) && selectTop > 0;

  if (isSelectTopDownload && hasValidSelectTop) {
    return sourceCompanyIds.slice(0, selectTop);
  }

  return sourceCompanyIds;
};

// 获取厂商接口参数
const getCompaniesApiParams = (params: any) => {
  if (!params) return {};

  const groupBys = ['company_id'];

  // 处理 Time Settings 和 Region Settings
  groupByGenerators.forEach((generator) => {
    const formModelKey = generator?.formModelKey;
    const groupByKey = generator?.groupByKey;
    const condition = generator?.condition;
    if (!!condition?.(params?.[formModelKey])) {
      groupBys.push(groupByKey);
    }
  });

  const useMarketInfo = (() => {
    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'market') {
      return ['market -> region', 'market -> en', 'market -> cn', 'intel_region -> en', 'intel_region -> cn'];
    }

    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'region') {
      return ['intel_region -> en', 'intel_region -> cn'];
    }

    return [];
  })();

  const orderBy = params?.order_by || ['sensortower__daily_avg_dau desc'];
  const isSelectTopDownload = params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] === 'selectTop';
  const selectTop = Number(params?.[DOWNLOAD_CFG_KEY.SELECT_TOP]);
  const hasValidSelectTop = Number.isFinite(selectTop) && selectTop > 0;

  const apiParams = {
    ...omit(params, [
      ...groupByFormModelKeys,
      DOWNLOAD_CFG_KEY.EDITION_METHOD,
      DOWNLOAD_CFG_KEY.DATA_METHOD,
      DOWNLOAD_CFG_KEY.SELECT_TOP,
      'ratio_show',
      'data_amount',
      'file_format',
      'file_name',
      'new_style_donwload_file_name',
      'ratio',
      'regionOptions',
      'companyInfo',
      'hasRevenue',
      'page_company_ids',
      'page_company_game_ids_map',
    ]),
    group_by: groupBys,
    order_by: orderBy,
    page_size: MAX_ROWS_LIMIT, // 按日期/地区拆分时，同一厂商会有多条记录
    use_entity_type: 'company',
    use_custom_fields: ['name', 'image_url', 'country_abbre', 'country_name_en'],
    use_market_info: useMarketInfo,
    // 使用厂商指标
    metrics: params?.company_metrics || params?.metrics,
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
    filter: {
      ...(params?.filter || {}),
      enable_game_id_filter: false,
      enable_genre_filter: true,
      enable_company_id_filter: true,
    },
    hasRevenue: params?.hasRevenue,
    top: {
      group_by: ['company_id'],
      order_by: orderBy,
      size: selectTop,
    },
  };

  if (!isSelectTopDownload || !hasValidSelectTop) {
    Reflect.deleteProperty(apiParams, 'top');
  }

  return apiParams;
};

// 获取游戏接口参数
const getGamesApiParams = (params: any) => {
  if (!params) return {};

  const groupBys = ['company_id', 'game_id'];

  // 处理 Time Settings 和 Region Settings
  groupByGenerators.forEach((generator) => {
    const formModelKey = generator?.formModelKey;
    const groupByKey = generator?.groupByKey;
    const condition = generator?.condition;
    if (!!condition?.(params?.[formModelKey])) {
      groupBys.push(groupByKey);
    }
  });

  const useMarketInfo = (() => {
    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'market') {
      return ['market -> region', 'market -> en', 'market -> cn', 'intel_region -> en', 'intel_region -> cn'];
    }

    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'region') {
      return ['intel_region -> en', 'intel_region -> cn'];
    }

    return [];
  })();

  const selectedCompanyIds = getSelectedCompanyIds(params);

  return {
    ...omit(params, [
      ...groupByFormModelKeys,
      DOWNLOAD_CFG_KEY.EDITION_METHOD,
      DOWNLOAD_CFG_KEY.DATA_METHOD,
      DOWNLOAD_CFG_KEY.SELECT_TOP,
      'ratio_show',
      'data_amount',
      'file_format',
      'file_name',
      'new_style_donwload_file_name',
      'ratio',
      'regionOptions',
      'companyInfo',
      'hasRevenue',
      'page_company_ids',
      'page_company_game_ids_map',
    ]),
    group_by: groupBys,
    order_by: ['sensortower__daily_avg_dau desc'],
    page_size: MAX_ROWS_LIMIT,
    use_custom_fields: [
      'entity_name',
      'cover',
      'publisher',
      'country_abbre',
      'country_name_en',
      'sensortower_sub_genre',
    ],
    use_market_info: useMarketInfo,
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
    filter: {
      ...(params?.filter || {}),
      company_ids: selectedCompanyIds,
      enable_game_id_filter: false,
      enable_genre_filter: true,
      enable_dau_share_by_company_games: true,
    },
    hasRevenue: params?.hasRevenue,
  };
};

export default (params: any) => {
  const companiesApiParams = getCompaniesApiParams(params);
  const gamesApiParams = getGamesApiParams(params);
  // 只要任意一个接口启用 SSE，就使用 SSE 模式
  const useSSE = shouldUseSSE(companiesApiParams) || shouldUseSSE(gamesApiParams);

  return {
    ...commonSetting,
    downloadMode: 'commonConf',
    downloadConfigKey: 'casualCompetitorsTopPublishers',
    // 不使用单个 downloadFetchApi，而是使用 downloadApiListObj
    downloadFetchApi: '',
    // downloadApiParams 用于传递参数给 Excel 配置函数
    downloadApiParams: gamesApiParams,
    // 配置多个接口
    downloadApiListObj: {
      companies: {
        api: '/api/v1/intelligence_pc/studio_hub/competitor/metrics',
        apiParams: companiesApiParams,
      },
      games: {
        api: '/api/v1/intelligence_pc/studio_hub/competitor/metrics',
        apiParams: gamesApiParams,
      },
    },
    isUsingNewMethod: false,
    fetchOnlyOnce: !useSSE,
    dataKey: ['Companies', 'Games'],
    $$fileName: getFileName(params),
    regionOptions: params?.regionOptions,
    hasRevenue: params?.hasRevenue,
    granularity: params?.granularity,
  };
};
